/**
 * Feedback Modal Component
 * A modal with a form for users to submit feedback, bug reports, or feature requests.
 */
import api from '../../services/api.js'
import { getUser } from '../../services/auth.js'

export function FeedbackModal() {
    const user = getUser()

    // Create overlay
    const overlay = document.createElement('div')
    overlay.id = 'feedback-modal-overlay'
    overlay.className = `
    fixed inset-0 z-50
    bg-black/50 backdrop-blur-sm
    flex items-center justify-center p-4
    animate-fade-in
  `.replace(/\s+/g, ' ').trim()

    // Create modal
    const modal = document.createElement('div')
    modal.className = `
    bg-white rounded-2xl shadow-2xl
    w-full max-w-md
    transform transition-all duration-200
    animate-slide-up
  `.replace(/\s+/g, ' ').trim()

    modal.innerHTML = `
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-semibold text-slate-800">Send Feedback</h2>
        <button id="close-feedback-modal" class="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- Form -->
      <form id="feedback-form" class="space-y-4">
        <!-- Category -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Category</label>
          <select id="feedback-category" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors">
            <option value="bug">🐛 Bug Report</option>
            <option value="feature">✨ Feature Request</option>
            <option value="other">💬 Other</option>
          </select>
        </div>
        
        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input 
            type="email" 
            id="feedback-email" 
            value="${user?.email || ''}"
            placeholder="your@email.com"
            class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            required
          />
        </div>
        
        <!-- Subject -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Subject</label>
          <input 
            type="text" 
            id="feedback-subject" 
            placeholder="Brief description"
            class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            required
          />
        </div>
        
        <!-- Message -->
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">Message</label>
          <textarea 
            id="feedback-message" 
            rows="4"
            placeholder="Tell us more details..."
            class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
            required
          ></textarea>
        </div>
        
        <!-- Submit Button -->
        <button 
          type="submit" 
          id="feedback-submit"
          class="w-full py-3 px-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          Send Feedback
        </button>
        
        <!-- Status Message -->
        <div id="feedback-status" class="hidden text-center py-2 rounded-lg"></div>
      </form>
    </div>
  `

    overlay.appendChild(modal)

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            overlay.remove()
        }
    })

    // Close button
    setTimeout(() => {
        const closeBtn = modal.querySelector('#close-feedback-modal')
        if (closeBtn) {
            closeBtn.addEventListener('click', () => overlay.remove())
        }

        // Form submission
        const form = modal.querySelector('#feedback-form')
        if (form) {
            form.addEventListener('submit', async (e) => {
                e.preventDefault()

                const submitBtn = modal.querySelector('#feedback-submit')
                const statusDiv = modal.querySelector('#feedback-status')

                // Disable button and show loading
                submitBtn.disabled = true
                submitBtn.innerHTML = `
          <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Sending...
        `

                const payload = {
                    email: modal.querySelector('#feedback-email').value,
                    subject: modal.querySelector('#feedback-subject').value,
                    message: modal.querySelector('#feedback-message').value,
                    category: modal.querySelector('#feedback-category').value
                }

                try {
                    await api.post('/support/', payload)

                    // Show success
                    statusDiv.className = 'text-center py-2 rounded-lg bg-green-100 text-green-700'
                    statusDiv.textContent = '✓ Thank you! Your feedback has been sent.'
                    statusDiv.classList.remove('hidden')

                    // Reset form
                    form.reset()

                    // Close after delay
                    setTimeout(() => overlay.remove(), 2000)

                } catch (error) {
                    console.error('Feedback submission error:', error)

                    // Show error
                    statusDiv.className = 'text-center py-2 rounded-lg bg-red-100 text-red-700'
                    statusDiv.textContent = 'Failed to send feedback. Please try again.'
                    statusDiv.classList.remove('hidden')

                    // Re-enable button
                    submitBtn.disabled = false
                    submitBtn.innerHTML = `
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Send Feedback
          `
                }
            })
        }
    }, 0)

    return overlay
}
